<template>
  <div class="custom-api-page">
    <!-- 顶部导航栏 -->
    <div class="header">
      <div class="header-left" @click="goBack">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="header-title">自定义接口</div>
      <div class="header-right"></div>
    </div>

    <!-- 提示信息 -->
    <div class="info-card">
      <div class="info-title">💡 使用说明</div>
      <div class="info-text">
        • 每个账户最多可添加 5 个自定义防红接口<br>
        • 支持完整链接格式（如：https://example.com/xy-chat?agent=xxx）<br>
        • 可随时编辑备注或删除接口
      </div>
      <div class="info-count">
        已添加 <span class="count-num">{{ customLinks.length }}</span> / 5
      </div>
    </div>

    <!-- 添加按钮 -->
    <div class="add-btn-container">
      <button
        class="add-btn"
        @click="openAddDialog"
        :disabled="customLinks.length >= 5"
      >
        <span class="add-icon">+</span>
        添加自定义接口
      </button>
    </div>

    <!-- 接口列表 -->
    <div class="api-list">
      <div v-if="isLoading" class="loading">加载中...</div>
      <div v-else-if="customLinks.length === 0" class="empty">
        <div class="empty-icon">📝</div>
        <div class="empty-text">暂无自定义接口</div>
        <div class="empty-tip">点击上方按钮添加您的第一个接口</div>
      </div>
      <div v-else class="api-items">
        <div
          v-for="(link, index) in customLinks"
          :key="link.id"
          class="api-item"
        >
          <div class="api-item-header">
            <div class="api-item-index">#{{ index + 1 }}</div>
            <div class="api-item-actions">
              <button class="action-btn edit-btn" @click="openEditDialog(link)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                编辑
              </button>
              <button class="action-btn delete-btn" @click="deleteLink(link)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                删除
              </button>
            </div>
          </div>
          <div class="api-item-body">
            <div class="api-item-label">备注</div>
            <div class="api-item-value">{{ link.name || '未命名' }}</div>
          </div>
          <div class="api-item-body">
            <div class="api-item-label">接口链接</div>
            <div class="api-item-value link-value">{{ link.apiUrl }}</div>
          </div>
          <div class="api-item-footer">
            <div class="api-item-time">创建时间: {{ formatDate(link.createTime) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加/编辑弹窗 -->
    <div v-if="showDialog" class="dialog-overlay" @click.self="closeDialog">
      <div class="dialog">
        <div class="dialog-header">
          <div class="dialog-title">{{ isEdit ? '编辑接口' : '添加接口' }}</div>
          <button class="dialog-close" @click="closeDialog">&times;</button>
        </div>
        <div class="dialog-body">
          <div class="form-group">
            <label class="form-label">
              接口链接 <span class="required">*</span>
            </label>
            <input
              v-model="formData.apiUrl"
              type="text"
              class="form-input"
              placeholder="例如：https://example.com/xy-chat?agent=xxx"
            />
            <div class="form-tip">输入完整的防红链接（包含 ?c= 编码参数或 /xy-chat?agent=xxx 格式）</div>
          </div>
          <div class="form-group">
            <label class="form-label">备注</label>
            <input
              v-model="formData.name"
              type="text"
              class="form-input"
              placeholder="给接口起个名字方便识别"
            />
          </div>
        </div>
        <div class="dialog-footer">
          <button class="dialog-btn cancel-btn" @click="closeDialog">取消</button>
          <button
            class="dialog-btn confirm-btn"
            @click="submitForm"
            :disabled="isSubmitting || !formData.apiUrl.trim()"
          >
            {{ isSubmitting ? '提交中...' : '确定' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { apiFetch } from '@/config/api';

const customLinks = ref([]);
const isLoading = ref(false);
const showDialog = ref(false);
const isEdit = ref(false);
const isSubmitting = ref(false);
const currentEditId = ref(null);

const formData = ref({
  name: '',
  apiUrl: ''
});

// 返回上一页
const goBack = () => {
  window.history.back();
};

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

// 获取自定义接口列表
const fetchCustomLinks = async () => {
  isLoading.value = true;
  try {
    const response = await apiFetch('/api/custom-anti-red/list');
    if (response && response.code === 0) {
      customLinks.value = response.data || [];
    }
  } catch (error) {
    console.error('获取自定义接口失败:', error);
  } finally {
    isLoading.value = false;
  }
};

// 打开添加弹窗
const openAddDialog = () => {
  if (customLinks.value.length >= 5) {
    alert('最多只能添加5个自定义接口');
    return;
  }
  isEdit.value = false;
  formData.value = { name: '', apiUrl: '' };
  showDialog.value = true;
};

// 打开编辑弹窗
const openEditDialog = (link) => {
  isEdit.value = true;
  currentEditId.value = link.id;
  formData.value = {
    name: link.name || '',
    apiUrl: link.apiUrl || ''
  };
  showDialog.value = true;
};

// 关闭弹窗
const closeDialog = () => {
  showDialog.value = false;
  formData.value = { name: '', apiUrl: '' };
  currentEditId.value = null;
};

// 提交表单
const submitForm = async () => {
  if (!formData.value.apiUrl.trim()) {
    alert('请输入接口链接');
    return;
  }

  isSubmitting.value = true;
  try {
    let response;
    if (isEdit.value) {
      // 编辑接口
      response = await apiFetch(`/api/custom-anti-red/update/${currentEditId.value}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: formData.value.name.trim() || '自定义接口',
          apiUrl: formData.value.apiUrl.trim()
        })
      });
    } else {
      // 添加接口
      response = await apiFetch('/api/custom-anti-red/add', {
        method: 'POST',
        body: JSON.stringify({
          name: formData.value.name.trim() || '自定义接口',
          apiUrl: formData.value.apiUrl.trim()
        })
      });
    }

    if (response && response.code === 0) {
      alert(isEdit.value ? '编辑成功' : '添加成功');
      closeDialog();
      await fetchCustomLinks();
    } else {
      alert(response.msg || (isEdit.value ? '编辑失败' : '添加失败'));
    }
  } catch (error) {
    console.error('提交失败:', error);
    alert('操作失败，请重试');
  } finally {
    isSubmitting.value = false;
  }
};

// 删除接口
const deleteLink = async (link) => {
  if (!confirm(`确定删除「${link.name || '未命名'}」？`)) {
    return;
  }

  try {
    const response = await apiFetch(`/api/custom-anti-red/delete/${link.id}`, {
      method: 'DELETE'
    });

    if (response && response.code === 0) {
      alert('删除成功');
      await fetchCustomLinks();
    } else {
      alert(response.msg || '删除失败');
    }
  } catch (error) {
    console.error('删除失败:', error);
    alert('删除失败，请重试');
  }
};

onMounted(() => {
  fetchCustomLinks();
});
</script>

<style scoped>
.custom-api-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 20px;
}

/* 顶部导航栏 */
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  background: #fff;
  padding: 0 16px;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-left {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #333;
}

.header-title {
  font-size: 17px;
  font-weight: 500;
  color: #333;
}

.header-right {
  width: 40px;
}

/* 提示信息卡片 */
.info-card {
  margin: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  color: #fff;
}

.info-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
}

.info-text {
  font-size: 13px;
  line-height: 20px;
  opacity: 0.9;
  margin-bottom: 12px;
}

.info-count {
  font-size: 14px;
  font-weight: 500;
}

.count-num {
  font-size: 20px;
  font-weight: 700;
  margin: 0 4px;
}

/* 添加按钮 */
.add-btn-container {
  padding: 0 16px 16px;
}

.add-btn {
  width: 100%;
  height: 48px;
  background: #1677ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.add-btn:active {
  opacity: 0.85;
}

.add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-icon {
  font-size: 24px;
  line-height: 1;
}

/* 接口列表 */
.api-list {
  padding: 0 16px;
}

.loading, .empty {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  color: #666;
  margin-bottom: 8px;
}

.empty-tip {
  font-size: 13px;
  color: #999;
}

.api-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.api-item {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.api-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.api-item-index {
  font-size: 14px;
  font-weight: 600;
  color: #1677ff;
}

.api-item-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.edit-btn {
  background: #e6f7ff;
  color: #1677ff;
}

.edit-btn:active {
  background: #bae7ff;
}

.delete-btn {
  background: #fff1f0;
  color: #ff4d4f;
}

.delete-btn:active {
  background: #ffccc7;
}

.api-item-body {
  margin-bottom: 12px;
}

.api-item-body:last-of-type {
  margin-bottom: 8px;
}

.api-item-label {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.api-item-value {
  font-size: 14px;
  color: #333;
  word-break: break-all;
}

.link-value {
  color: #1677ff;
}

.api-item-footer {
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.api-item-time {
  font-size: 12px;
  color: #999;
}

/* 弹窗 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.dialog {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.dialog-title {
  font-size: 17px;
  font-weight: 600;
  color: #333;
}

.dialog-close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.dialog-body {
  padding: 20px;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
}

.required {
  color: #ff4d4f;
}

.form-input {
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #1677ff;
}

.form-tip {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
  line-height: 18px;
}

.dialog-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
}

.dialog-btn {
  flex: 1;
  height: 44px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn {
  background: #f5f5f5;
  color: #666;
}

.cancel-btn:active {
  background: #e8e8e8;
}

.confirm-btn {
  background: #1677ff;
  color: #fff;
}

.confirm-btn:active {
  opacity: 0.85;
}

.confirm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
